import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useSnap } from '../hooks/useSnap.js';
import { useWallet } from '../hooks/useWallet.js';
import { resolveInstallState, type InstallState } from '../lib/installState.js';
import { trackEvent } from '../lib/analytics.js';
import { trackConversion } from '../services/conversionTracking.js';

function checkSubscriptionStatus(
  token: string | null,
): Promise<'active' | 'inactive' | 'unknown'> {
  if (!token) return Promise.resolve('unknown');
  return fetch('/api/subscription/status', {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => (d?.status === 'active' ? 'active' : 'inactive'))
    .catch(() => 'unknown' as const);
}

export function SubscriptionSuccess() {
  const snap = useSnap();
  const wallet = useWallet();
  const [subStatus, setSubStatus] = useState<'unknown' | 'active' | 'inactive'>('unknown');
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    document.title = 'CryptoGuardians — Installation Required';
    trackEvent('stripe_success');
    trackConversion('checkout_completed');
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!wallet.token || !wallet.address) return;
    setChecking(true);
    const status = await checkSubscriptionStatus(wallet.token);
    setSubStatus(status);
    if (status === 'active') {
      trackEvent('subscription_verified');
    }
    setChecking(false);
  }, [wallet.token, wallet.address]);

  useEffect(() => {
    if (wallet.isConnected) {
      checkSubscription();
    }
  }, [wallet.isConnected, checkSubscription]);

  const state: InstallState = resolveInstallState({
    hasMetaMask: snap.isMetaMask,
    isSnapInstalled: snap.isInstalled,
    isWalletConnected: wallet.isConnected,
    subscriptionStatus: subStatus,
  });

  return (
    <div className="sub-result">
      <div className="card sub-result-card">
        <div className="sub-result-icon" aria-hidden="true">&#x2705;</div>
        <h1>Subscription Activated!</h1>
        <p>
          Your CryptoGuardians Pro subscription is now active. Complete the steps below to enable
          protection.
        </p>

        <div className="success-steps">
          {/* Step 1: Snap Install */}
          <div className={`success-step ${snap.isInstalled ? 'success-step-done' : state === 'snap_not_installed' || state === 'no_metamask' ? 'success-step-active' : ''}`}>
            <div className="success-step-num">{snap.isInstalled ? '\u2713' : '1'}</div>
            <div className="success-step-body">
              <h3>Install MetaMask Snap</h3>
              {state === 'no_metamask' && (
                <p>
                  MetaMask is required.{' '}
                  <a href="https://metamask.io" target="_blank" rel="noopener noreferrer">
                    Download MetaMask
                  </a>
                </p>
              )}
              {state === 'snap_not_installed' && (
                <>
                  <p>Install the CryptoGuardians Snap to enable real-time protection.</p>
                  <button
                    className="btn btn-primary"
                    onClick={snap.installSnap}
                    disabled={snap.isLoading}
                  >
                    {snap.isLoading ? 'Installing...' : 'Install Snap'}
                  </button>
                  {snap.error && <p className="error-message" style={{ marginTop: '0.5rem' }}>{snap.error}</p>}
                </>
              )}
              {snap.isInstalled && <p className="success-step-check">Snap installed</p>}
            </div>
          </div>

          {/* Step 2: Connect Wallet */}
          <div className={`success-step ${wallet.isConnected ? 'success-step-done' : state === 'snap_installed_not_connected' ? 'success-step-active' : ''}`}>
            <div className="success-step-num">{wallet.isConnected ? '\u2713' : '2'}</div>
            <div className="success-step-body">
              <h3>Connect Wallet</h3>
              {state === 'snap_installed_not_connected' && (
                <>
                  <p>Sign in with your wallet to activate your subscription.</p>
                  <button
                    className="btn btn-primary"
                    onClick={wallet.connect}
                    disabled={wallet.isConnecting}
                  >
                    {wallet.isConnecting ? 'Connecting...' : 'Connect Wallet'}
                  </button>
                  {wallet.error && <p className="error-message" style={{ marginTop: '0.5rem' }}>{wallet.error}</p>}
                </>
              )}
              {wallet.isConnected && <p className="success-step-check">Wallet connected</p>}
            </div>
          </div>

          {/* Step 3: Verification */}
          <div className={`success-step ${state === 'verified_active' ? 'success-step-done' : state === 'connected_not_verified' || state === 'verified_inactive' ? 'success-step-active' : ''}`}>
            <div className="success-step-num">{state === 'verified_active' ? '\u2713' : '3'}</div>
            <div className="success-step-body">
              <h3>Verify Subscription</h3>
              {checking && <p>Checking subscription status...</p>}
              {state === 'verified_active' && (
                <p className="success-step-check">Protection Active</p>
              )}
              {state === 'verified_inactive' && (
                <>
                  <p>Subscription not yet detected for this wallet.</p>
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={checkSubscription}>
                      Retry
                    </button>
                    <Link to="/support" className="btn btn-secondary">
                      Contact Support
                    </Link>
                  </div>
                </>
              )}
              {state === 'connected_not_verified' && !checking && (
                <p>Verifying your subscription...</p>
              )}
            </div>
          </div>
        </div>

        {state === 'verified_active' && (
          <div className="sub-result-actions" style={{ marginTop: '2rem' }}>
            <Link to="/wallet" className="btn btn-primary btn-lg btn-primary-glow">
              Go to Dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
